import { Injectable } from '@angular/core';
import { Observable, map, throwError } from 'rxjs';
import { ApiClass } from '@data/class/api-class';
import { 
  Billing, 
  CreateBillingDto, 
  UpdateBillingDto, 
  BillingFilters, 
  PaginatedResponse 
} from '../../interfaces/billing.interface';

@Injectable({
  providedIn: 'root'
})
export class BillingService extends ApiClass {
  
  constructor() {
    super();
    this.path = 'billing';
  }

  /**
   * Mapear errores específicos basados en el mensaje del ApiClass
   */
  private mapApiError(response: any): Error {
    console.error('Error del API Billing:', response);
    
    const errorMsg = response.msg || 'Error desconocido';
    
    // Error de permisos
    if (errorMsg.includes('No cuentas con los permisos necesarios') || 
        errorMsg.includes('permisos necesarios') ||
        errorMsg.includes('permisos insuficientes')) {
      return new Error('No tienes permisos suficientes para realizar esta acción. Contacta al administrador.');
    }
    
    // Error de validación
    if (errorMsg.includes('validación') || errorMsg.includes('validation')) {
      return new Error(errorMsg);
    }
    
    // Error no encontrado
    if (errorMsg.includes('no existe') || errorMsg.includes('not found')) {
      return new Error('La información de facturación no fue encontrada');
    }
    
    // Error de conexión
    if (errorMsg.includes('conexión') || errorMsg.includes('connection')) {
      return new Error('Error de conexión. Intenta nuevamente más tarde');
    }
    
    // Error general
    return new Error(errorMsg);
  }

  /**
   * Crea información de facturación
   */
  createBilling(data: CreateBillingDto): Observable<Billing> {
    return this.postCollection(data, 'create').pipe(
      map((response) => {
        if (!response.error) {
          return response.data;
        } else {
          // El ApiClass ya procesó el error, solo necesitamos lanzarlo
          throw this.mapApiError(response);
        }
      })
    );
  }

  /**
   * Actualiza información de facturación
   */
  updateBilling(billingId: string, data: UpdateBillingDto): Observable<Billing> {
    return this.putCollection(data, undefined, `billing/${billingId}`).pipe(
      map((response) => {
        if (!response.error) {
          return response.data;
        } else {
          // El ApiClass ya procesó el error, solo necesitamos lanzarlo
          throw this.mapApiError(response);
        }
      })
    );
  }

  /**
   * Obtiene información de facturación por ID
   */
  getBillingById(billingId: string): Observable<Billing> {
    return this.getCollection(billingId).pipe(
      map((response) => {
        if (!response.error) {
          return response.data;
        } else {
          // El ApiClass ya procesó el error, solo necesitamos lanzarlo
          throw this.mapApiError(response);
        }
      })
    );
  }

  /**
   * Lista todas las configuraciones de facturación
   */
  getAllBillings(filters: BillingFilters): Observable<PaginatedResponse<Billing>> {
    return this.postCollection(filters).pipe(
      map((response) => {
        if (!response.error) {
          return {
            results: response.data.data || [],
            meta: {
              page: response.data.page || 1,
              limit: response.data.itemsByPage || 10,
              total: response.data.totalItems || 0,
              totalPages: Math.ceil((response.data.totalItems || 0) / (response.data.itemsByPage || 10))
            }
          };
        } else {
          // El ApiClass ya procesó el error, solo necesitamos lanzarlo
          throw this.mapApiError(response);
        }
      })
    );
  }
}